import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import ButtonWithConfirmationDialog from 'components/buttons/ButtonWithConfirmationDialog';
import { ClampedTypography } from 'components/labels/ClampedTypography';
import { getBlueprintCalculations, useRevokeSharedBlueprint } from 'features/blueprints';
import { Icons } from 'greener-ui-library';
import { useSnackbar } from 'notistack';
import { Blueprint } from 'types';
import { DATE_FORMAT } from 'utils/constants';
import { BlueprintCalculationDetailsForm } from '../BlueprintCalculationDetailsForm';
import { BusinessCaseComparisonTable } from '../BusinessCaseComparisonTable';
import { DisclaimerBox } from '../DisclaimerBox';
import { MicrogridSetupBox } from '../MicrogridSetupBox';
import { PdfExportButton } from '../PdfExportButton';
import { PdfHeader } from '../PdfExportButton/PdfHeader';
import { PowerRequirementsBox } from '../PowerRequirementsBox';
import { ProposedSolutionBox } from '../ProposedSolutionBox';
import { getSetupDetails } from '../utils';

interface PdfSectionProps {
  pdfFileName: string;
  blueprintDetails: Blueprint;
}

export const PdfSection = ({ pdfFileName, blueprintDetails }: PdfSectionProps) => {
  const { t } = useTranslation(['blueprints']);
  const { hasGrid, hasKite } = getBlueprintCalculations(blueprintDetails);
  const hasEnergySources = hasGrid || hasKite;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2">{t('blueprints:share.pdfDocument')}</Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <ClampedTypography variant="body1" color="primary" maxLines={1}>
          {pdfFileName}
        </ClampedTypography>

        <PdfExportButton
          title={t('blueprints:share.downloadPdf')}
          filename={blueprintDetails.name}
          header={<PdfHeader blueprintDetails={blueprintDetails} />}
          microgridSetup={
            <MicrogridSetupBox
              imageSvg={getSetupDetails(blueprintDetails)?.imageSvg}
              setupType={getSetupDetails(blueprintDetails)?.setupType}
              sx={{ backgroundColor: 'grey.50', borderRadius: 0, boxShadow: 'none' }}
            />
          }
          proposedSolution={
            <ProposedSolutionBox
              blueprintDetails={blueprintDetails}
              readOnly
              compact
              sx={{ borderRadius: 0, boxShadow: 'none', py: 2.5, px: 6 }}
              titleSx={{ textAlign: 'center', width: '100%' }}
            />
          }
          businessCaseComparison={
            <BusinessCaseComparisonTable
              blueprintDetails={blueprintDetails}
              showProjectDates
              sx={{
                boxShadow: 'none',
                '#environmental-table': {
                  backgroundColor: 'transparent',
                },
                '#energy-sources-table': {
                  display: 'none',
                },
              }}
            />
          }
          energySources={
            hasEnergySources ? (
              <BusinessCaseComparisonTable
                blueprintDetails={blueprintDetails}
                showProjectDates
                sx={{
                  boxShadow: 'none',
                  '#costs-table': {
                    display: 'none',
                  },
                  '#environmental-table': {
                    display: 'none',
                  },
                }}
              />
            ) : null
          }
          powerRequirement={
            <PowerRequirementsBox
              blueprintDetails={blueprintDetails}
              readOnly
              sx={{ boxShadow: 'none' }}
              isAnimationActive={false}
            />
          }
          calculationDetails={
            <BlueprintCalculationDetailsForm blueprintDetails={blueprintDetails} readOnly sx={{ boxShadow: 'none' }} />
          }
          disclaimer={<DisclaimerBox />}
        />
      </Box>
    </Box>
  );
};

interface CopyableTextFieldProps {
  value: string;
  label: string;
  onCopy: () => void;
  isCopied: boolean;
  maxWidth?: Record<string, string>;
  flex?: Record<string, string>;
}

export const CopyableTextField = ({
  value,
  label,
  onCopy,
  isCopied,
  maxWidth = { xs: '100%', sm: '448px' },
  flex = { xs: '1 1 100%', lg: '1 1 auto' },
}: CopyableTextFieldProps) => {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex }}>
      <TextField
        data-testid="copyable-text-field"
        value={value}
        label={label}
        fullWidth
        size="small"
        InputProps={{
          readOnly: true,
        }}
        InputLabelProps={{
          shrink: true,
        }}
        sx={{
          maxWidth,
        }}
      />
      <IconButton onClick={onCopy} color={isCopied ? 'primary' : 'default'}>
        {isCopied ? <Icons.Check /> : <Icons.ContentCopy />}
      </IconButton>
    </Box>
  );
};

interface ExpirationDatePickerProps {
  selectedDate: Date;
  onChange: (date: Date | null) => void;
  label: string;
  disabled?: boolean;
}

export const ExpirationDatePicker = ({
  selectedDate,
  onChange,
  label,
  disabled = false,
}: ExpirationDatePickerProps) => {
  return (
    <DatePicker
      selected={selectedDate}
      onChange={onChange}
      dateFormat={DATE_FORMAT}
      showTimeSelect={false}
      minDate={new Date()}
      disabled={disabled}
      popperPlacement="bottom"
      customInput={
        <TextField
          data-testid="expiration-date-picker"
          label={label}
          variant="outlined"
          disabled={disabled}
          size="small"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Icons.ArrowDropDown />
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{
            maxWidth: { xs: 'calc(100% - 48px)', sm: 'calc(100% - 104px)' },
            width: '100%',
          }}
        />
      }
    />
  );
};

interface DeactivateLinkButtonProps {
  onDeactivate: () => void;
  shareToken: string;
  blueprintId: number;
}

export const DeactivateLinkButton = ({ onDeactivate, shareToken, blueprintId }: DeactivateLinkButtonProps) => {
  const { t } = useTranslation(['blueprints']);
  const { enqueueSnackbar } = useSnackbar();
  const { revokeSharedBlueprint, isLoading: isRevoking } = useRevokeSharedBlueprint();

  const handleConfirm = async () => {
    try {
      await revokeSharedBlueprint({ shareToken, blueprintId });
      onDeactivate();
      enqueueSnackbar(t('blueprints:share.deactivateSuccess'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('blueprints:share.errors.deactivateFailed'), { variant: 'error' });
    }
  };

  return (
    <ButtonWithConfirmationDialog
      buttonProps={{
        color: 'error',
        disabled: isRevoking,
        sx: { pl: { xs: 0 } },
      }}
      buttonText={isRevoking ? <CircularProgress size={20} color="inherit" /> : t('blueprints:share.deactivateLink')}
      handleConfirm={handleConfirm}
      title={t('blueprints:share.deactivateConfirmation.title')}
      confirmButtonText={t('blueprints:share.deactivateConfirmation.confirm')}
      cancelButtonText={t('blueprints:share.deactivateConfirmation.cancel')}
      dialogConfirmButtonProps={{ disabled: isRevoking }}
    >
      <Typography variant="body1">{t('blueprints:share.deactivateConfirmation.message')}</Typography>
    </ButtonWithConfirmationDialog>
  );
};
